import './_trust-details-section.scss';
import React, {useEffect, useState} from 'react';
import RadioGroupField from 'src/EstateFlowchart/components/FormContent/RadioGroupField/RadioGroupField';
import {PowerOfAppointment} from 'src/EstateFlowchart/components/PowersOfAppointmentSection/PowerOfAppointment';
import PowersOfAppointmentSection
    from 'src/EstateFlowchart/components/PowersOfAppointmentSection/PowersOfAppointmentSection';
import {TagsTooltipContent} from 'src/EstateFlowchart/components/TagsTooltipContent/TagsTooltipContent';
import {IrrevocableTrust} from 'src/EstateFlowchart/model/IrrevocableTrust';
import {useAppSelector} from "../../../store/hooks";
import DropdownField from "../../components/FormContent/DropdownField/DropdownField";
import InputField from "../../components/FormContent/InputField/InputField";
import MemberDropdownMultiselect
    from "../../components/FormContent/MemberDropdownMultiselect/MemberDropdownMultiselect";
import TextAreaField from "../../components/FormContent/TextAreaField/TextAreaField";
import FormPageSection from "../../components/FormPageSection/FormPageSection";
import SuccessorTable from "../../components/SuccessorTable/SuccessorTable";
import {TRUST_DETAILS_CONSTANTS as Labels, TRUST_FORM_CONSTANTS} from "../../constants/LabelConstants";
import {FamilyMembers, MemberType, RevocableTrust, State, Trustee} from "../../model/EstateFlowchartTypes";
import {familyMembersSelector, listOfStatesSelector} from "../../slices/EstateFlowChartSlice";
import {saveSelector} from "../../slices/NavigationSlice";
import {
    createFamilyMemberDropdownItems,
    evaluateDropdownMemberId,
    isCustomMember,
    parseFamilyMembers,
    parseStates
} from "../../utils/DefUtils";
import LegalDocumentsSection from './LegalDocumentsSection';
import {selectReleaseToggles} from "../../../ReleaseToggles/releaseTogglesSlice";

interface TrustDetailsSectionProps {
    estateItem: RevocableTrust | IrrevocableTrust;
    updateEstateItem: (estateItem: RevocableTrust | IrrevocableTrust) => void;
}

function TrustDetailsSection({estateItem, updateEstateItem}: Readonly<TrustDetailsSectionProps>) {
    const saveHasBeenClicked = useAppSelector(saveSelector);
    let dropdownHeight: number = 0;
    const dropdownHeightMax = 280;
    const dropdownItemHeight = 34;

    const rawFamilyMembers = useAppSelector(familyMembersSelector);
    const unitedStates = useAppSelector(listOfStatesSelector);
    const [parsedFamilyMembers, setParsedFamilyMembers] = useState<FamilyMembers>({value: "", label: "", familyMember: []});
    const [parsedStates, setParsedStates] = useState<State[]>([]);
    const isIrrevocableTrust = 'terminatesUponSpecificDeath' in estateItem;
    const releaseToggles = useAppSelector(selectReleaseToggles);

    useEffect(() => {
        buildFamilyMemberDropdownItems();
        const numMembers = parsedFamilyMembers?.familyMember?.length ?? 0;
        dropdownHeight = Math.min(numMembers * dropdownItemHeight, dropdownHeightMax);
    }, [rawFamilyMembers]);

    useEffect(() => {
        buildListOfStatesDropdownItems();
    }, [unitedStates]);

    const onChangeDropdown = (selectedOptions: any, type: MemberType) => {
        const members: Trustee[] = [];
        for (const element of selectedOptions) {
            const member: Trustee = {
                trustId: estateItem.trustId,
                memberType: type,
                familyMember: !isCustomMember(element.value, parsedFamilyMembers),
                customName: isCustomMember(element.value, parsedFamilyMembers) ? element.label : "",
                memberId: evaluateDropdownMemberId(element, parsedFamilyMembers),
            };
            members.push(member);
        }
        switch (type) {
            case MemberType.Grantor: {
                const newGrantors = {...estateItem, grantors: members};
                updateEstateItem(newGrantors);
                break;
            }
            case MemberType.Trustee: {
                const newTrustees = {...estateItem, trustees: members};
                updateEstateItem(newTrustees);
                break;
            }
            case MemberType.Beneficiary: {
                if (isIrrevocableTrust) {
                    const newBeneficiaries = {...estateItem, beneficiaries: members};
                    updateEstateItem(newBeneficiaries);
                }
                break;
            }
            default:
        }
    }

    const buildFamilyMemberDropdownItems = () => {
        const parsedFamilyMembersResponse: FamilyMembers = parseFamilyMembers(rawFamilyMembers);
        let item = {
            label: parsedFamilyMembersResponse.label,
            value: parsedFamilyMembersResponse.value,
            selected: false
        };
        parsedFamilyMembersResponse.familyMember.push(item);
        setParsedFamilyMembers(parsedFamilyMembersResponse);
    }

    const buildListOfStatesDropdownItems = () => {
        const parsedStates = parseStates(unitedStates);
        setParsedStates(parsedStates);
    }

    const sortedPowersOfAppointments = (): PowerOfAppointment[] => {
        if ('powersOfAppointment' in estateItem && estateItem.powersOfAppointment) {
            return [...estateItem.powersOfAppointment].sort((a, b) => a.poaOrder - b.poaOrder)
        }
        return [];
    }

    const updatePowersOfAppointments = (newPowersOfAppointments: PowerOfAppointment[]) => {
        if (isIrrevocableTrust && 'tags' in estateItem) {
            const updatedTrustDetails: IrrevocableTrust = {
                ...estateItem,
                powersOfAppointment: newPowersOfAppointments
            };
            updateEstateItem(updatedTrustDetails);
        }
    }

    return (
        <FormPageSection id={"trust-details-section"}
                         headerText={"Trust Details"}
                         showRequiredLabel={true}>

            {
                isIrrevocableTrust && (
                    <RadioGroupField label={TRUST_FORM_CONSTANTS.TAGS_LABEL_TEXT}
                                     id={"tags0"}
                                     values={["Exempt", "Non-Exempt", "Both", "Unknown/Inapplicable"]}
                                     selectedValue={estateItem.tags}
                                     required={true}
                                     tooltipContent={<TagsTooltipContent/>}
                                     onChange={(tags) => {
                                         updateEstateItem({
                                             ...estateItem,
                                             tags
                                         })
                                     }}/>
                )
            }

            <MemberDropdownMultiselect
                label="Grantors"
                required={true}
                options={rawFamilyMembers}
                selectedOptions={estateItem.grantors}
                includeNT={false}
                useTags={true}
                helperText='Add a new name, if it is not in the list.'
                onChange={(options: any) => onChangeDropdown(options, MemberType.Grantor)}
                onError={saveHasBeenClicked &&
                (!estateItem.grantors || estateItem.grantors.length === 0) ?
                    "Grantors is required." :
                    undefined
                }
            />

            <MemberDropdownMultiselect
                label="Trustees"
                required={true}
                options={rawFamilyMembers}
                selectedOptions={estateItem.trustees}
                includeNT={true}
                useTags={true}
                helperText='Add a new name, if it is not in the list.'
                onChange={(options: any) => onChangeDropdown(options, MemberType.Trustee)}
                onError={saveHasBeenClicked &&
                (!estateItem.trustees || estateItem.trustees.length === 0) ?
                    "Trustees is required." :
                    undefined
                }
            />

            {
                parsedFamilyMembers.familyMember && parsedFamilyMembers.familyMember.length > 0 &&
                <SuccessorTable formData={estateItem}
                                successorType={MemberType.Successor}
                                dropdownHeight={dropdownHeight}
                                updateData={updateEstateItem}
                                showSubtitle={true}/>
            }

            {
                isIrrevocableTrust && (
                    <MemberDropdownMultiselect
                        label="Beneficiaries"
                        required={false}
                        options={rawFamilyMembers}
                        selectedOptions={estateItem.beneficiaries}
                        includeNT={false}
                        useTags={true}
                        helperText='Add a new name, if it is not in the list.'
                        onChange={(options: any) => onChangeDropdown(options, MemberType.Beneficiary)}
                    />
                )
            }

            <TextAreaField input={estateItem.funding}
                           label={Labels.FUNDING_LABEL_TEXT}
                           subText={Labels.FUNDING_SUB_TEXT}
                           onChange={(funding) => updateEstateItem({
                               ...estateItem,
                               funding
                           })}/>

            {isIrrevocableTrust && (
                <TextAreaField
                    label={Labels.DISTRIBUTIONS_LABEL_TEXT}
                    input={('distributions' in estateItem ? estateItem.distributions : '')}
                    subText={TRUST_FORM_CONSTANTS.DISTRIBUTIONS_SUB_TEXT}
                    required={true}
                    onChange={(distributions) => {
                        updateEstateItem({
                            ...estateItem,
                            ...(isIrrevocableTrust ? {distributions} : {})
                        })
                    }}
                />
            )}

            {
                isIrrevocableTrust && (
                    <RadioGroupField label={Labels.TRUST_TERMINATION_LABEL_TEXT}
                                     id={"terminatesUponSpecificDeath"}
                                     className='terminates-upon-specific-death'
                                     values={["No", "Yes"]}
                                     selectedValue={estateItem.terminatesUponSpecificDeath ? "Yes" : "No"}
                                     required={false}
                                     onChange={(value) => {
                                        updateEstateItem({
                                            ...estateItem,
                                            terminatesUponSpecificDeath: value === "Yes",
                                            firstDeathTerminationMember: value === "Yes"
                                                ? estateItem.firstDeathTerminationMember
                                                : undefined,
                                            terminationDetails: value === "Yes" ? undefined : estateItem.terminationDetails,
                                            firstDeathBeneficiaries: value === "Yes" ? estateItem.firstDeathBeneficiaries : undefined
                                        });
                                     }}
                    />
                )
            }
            {
                'terminatesUponSpecificDeath' in estateItem && !estateItem.terminatesUponSpecificDeath && (
                    <TextAreaField input={estateItem.terminationDetails}
                                   label={"termination"}
                                   dataTestId='termination-field'
                                   onChange={(terminationDetails) => updateEstateItem({
                                       ...estateItem,
                                       terminationDetails
                                   })}/>
                )
            }
            {
                'terminatesUponSpecificDeath' in estateItem && estateItem.terminatesUponSpecificDeath && (
                    <DropdownField value={estateItem.firstDeathTerminationMember?.memberId ?? ""}
                                   label={Labels.WHOSE_DEATH_LABEL_TEXT}
                                   required={true}
                                   items={createFamilyMemberDropdownItems(parsedFamilyMembers.familyMember,
                                       estateItem.firstDeathTerminationMember?.customName)}
                                   onChange={(firstDeathTerminationMemberId) => updateEstateItem({
                                       ...estateItem,
                                       firstDeathTerminationMember: firstDeathTerminationMemberId != estateItem.firstDeathTerminationMember?.memberId ? {
                                           trustId: estateItem.trustId,
                                           memberType: MemberType.FirstDeath,
                                           memberId: firstDeathTerminationMemberId,
                                           customName: releaseToggles?.enableEstateFlowchartMVPRelease ? estateItem.firstDeathTerminationMember?.customName : undefined,
                                           familyMember: !isCustomMember(firstDeathTerminationMemberId, parsedFamilyMembers)
                                       } : estateItem.firstDeathTerminationMember
                                   })}
                    />)
            }
            <InputField label={Labels.INVESTMENT_ADVISOR_LABEL_TEXT}
                        name={estateItem.investmentAdvisor}
                        onChange={(investmentAdvisor) => {
                            updateEstateItem({
                                ...estateItem,
                                investmentAdvisor
                            })
                        }}/>

            <InputField label={Labels.TRUST_PROTECTOR_LABEL_TEXT}
                        name={estateItem.trustProtector}
                        onChange={(trustProtector) => updateEstateItem({
                            ...estateItem,
                            trustProtector
                        })}/>

            <DropdownField value={estateItem.trustJurisdiction ?? ""}
                           label={Labels.TRUST_JURISDICTION_LABEL_TEXT}
                           items={parsedStates}
                           onChange={(trustJurisdiction) => {
                               if (trustJurisdiction !== "") {
                                   updateEstateItem({
                                       ...estateItem,
                                       trustJurisdiction
                                   })
                               }
                           }}/>

            {
                isIrrevocableTrust && (
                    <PowersOfAppointmentSection powersOfAppointment={sortedPowersOfAppointments()}
                                                familyMembers={parsedFamilyMembers.familyMember}
                                                updateTrust={updatePowersOfAppointments}
                                                trustIndex={0}/>
                )
            }
            <LegalDocumentsSection/>
        </FormPageSection>
    );
}

export default TrustDetailsSection;